import './index.scss'

import React from 'react'
import { Outlet, Route, Routes, useParams, useSearchParams } from 'react-router-dom'
// Check for logged-in/logged-out user
import { RUNTIME_ENVIRONMENT, UUID } from 'src/dataLayer/env'
import { AppConfigProvider } from 'src/viewLayer/components/contexts/AppConfigContext'
import { CaptchaConfigProvider } from 'src/viewLayer/components/contexts/CaptchaConfigContext'
// Providers
import { RouteParamsProvider } from 'src/viewLayer/components/contexts/RouteParamsContext'
import { BabbelFonts } from 'src/viewLayer/components/DesignSystem'
import AuthenticatedRedirect from 'src/viewLayer/components/redirects/AuthenticatedRedirect'
// Redirects
import DefaultLoginRedirect from 'src/viewLayer/components/redirects/DefaultLoginRedirect'
import UnauthorizedBusinessRedirect from 'src/viewLayer/components/redirects/UnauthorizedBusinessRedirect'

import { useStatsigExperiment } from '../hooks/useStatsigExperiment'
import PasswordResetPage from '../pages/PasswordResetPage'
import SuccessCallbackPage from './Authenticated/SuccessCallbackPage'
import BusinessErrorPages from './BusinessErrorPages'
import BusinessPages from './BusinessPages'
import CollectPerformanceMetrics from './CollectPerformanceMetrics'
import ForgotPasswordPage from './ForgotPasswordPage'
import { AppComponentWithBackground } from './index.styled'
// Pages Component
import LoginPages from './LoginPages'
import NativeMobileLoginPages from './NativeMobileLoginPages'
import { BabbelThemeProviderMobile, BabbelThemeProviderWeb } from './ThemeProviders'

const RouterParametersWrapper = (properties) => {
  const { environment, locale } = useParams()
  const [searchParameters] = useSearchParams()
  return (
    <RouteParamsProvider
      environment={environment}
      locale={locale}
      queryParams={searchParameters.toString()}
      {...properties}
    >
      <CaptchaConfigProvider>
        <Outlet />
      </CaptchaConfigProvider>
    </RouteParamsProvider>
  )
}
const EmailLoginRouter = () => (
  <BabbelThemeProviderWeb>
    <AppConfigProvider devicePath="login">
      <LoginPages appType="login" />
    </AppConfigProvider>
  </BabbelThemeProviderWeb>
)
const ForgotPasswordRouter = () => (
  <BabbelThemeProviderWeb>
    <AppConfigProvider devicePath="login">
      <ForgotPasswordPage appType="login" />
    </AppConfigProvider>
  </BabbelThemeProviderWeb>
)
const PasswordResetRouter = () => (
  <BabbelThemeProviderWeb>
    <AppConfigProvider devicePath="login">
      <PasswordResetPage appType="login" />
    </AppConfigProvider>
  </BabbelThemeProviderWeb>
)
const BusinessRouter = () => (
  <BabbelThemeProviderWeb>
    <AppConfigProvider devicePath="login">
      <Routes>
        <Route element={<BusinessPages appType="login" />} path="/" />
        <Route element={<BusinessErrorPages appType="login" />} path="error" />
        <Route element={<UnauthorizedBusinessRedirect />} path="*" />
      </Routes>
    </AppConfigProvider>
  </BabbelThemeProviderWeb>
)
const NativeMobileLoginRouter = () => (
  <BabbelThemeProviderMobile>
    <AppConfigProvider devicePath="login-mobile">
      <NativeMobileLoginPages appType="webview-login" />
    </AppConfigProvider>
  </BabbelThemeProviderMobile>
)
const NativeMobileBusinessRouter = () => (
  <BabbelThemeProviderMobile>
    <AppConfigProvider devicePath="login-mobile">
      <Routes>
        <Route element={<BusinessPages appType="webview-login" />} path="/" />
        <Route element={<BusinessErrorPages appType="webview-login" />} path="error" />
        <Route element={<UnauthorizedBusinessRedirect />} path="*" />
      </Routes>
    </AppConfigProvider>
  </BabbelThemeProviderMobile>
)
const UnauthenticatedApp = () => (
  // TODO: Remove appType and use the path from AppConfigProvider
  <AppComponentWithBackground data-testid="unauthenticated-app">
    <BabbelFonts />
    <Routes>
      <Route element={<RouterParametersWrapper />} path=":locale/:environment">
        <Route element={<EmailLoginRouter />} path="login/*" />
        <Route element={<ForgotPasswordRouter />} path="login/forgot-password" />
        <Route element={<PasswordResetRouter />} path="login/password-reset" />
        <Route element={<BusinessRouter />} path="login/business/*" />
        <Route element={<NativeMobileLoginRouter />} path="login-mobile/*" />
        <Route element={<NativeMobileBusinessRouter />} path="login-mobile/business/*" />
        <Route element={<DefaultLoginRedirect />} path="*" />
        <Route element={<DefaultLoginRedirect />} path="" />
      </Route>
    </Routes>
  </AppComponentWithBackground>
)
const AuthenticatedNativeMobileBusinessRouter = () => (
  <BabbelThemeProviderMobile>
    <AppConfigProvider devicePath="login-mobile">
      <Routes>
        <Route element={<SuccessCallbackPage appType="webview-login" />} path="success" />
        <Route element={<AuthenticatedRedirect />} path="*" />
      </Routes>
    </AppConfigProvider>
  </BabbelThemeProviderMobile>
)
const AuthenticatedApp = () => (
  // TODO: Remove appType and use the path from AppConfigProvider
  // Authenticated App is for B2B SSO via Native WebView
  // any route that does not match this condition,
  // redirects to return_to (when available) or dashboard (when no return_to)
  // Valid routes are: login-mobile/business/error, login-mobile/business/success, and login/password-reset
  <AppComponentWithBackground data-testid="authenticated-app">
    <BabbelFonts />
    <Routes>
      <Route element={<RouterParametersWrapper />} path=":locale/:environment">
        <Route
          element={<AuthenticatedNativeMobileBusinessRouter />}
          path="login-mobile/business/*"
        />
        <Route element={<PasswordResetRouter />} path="login/password-reset" />
        <Route element={<AuthenticatedRedirect />} path="*" />
        <Route element={<AuthenticatedRedirect />} path="" />
      </Route>
    </Routes>
  </AppComponentWithBackground>
)
export default function App() {
  if (RUNTIME_ENVIRONMENT === 'development') {
    const { isExperimentTest } = useStatsigExperiment()
    // eslint-disable-next-line no-console -- Legacy
    console.log('StatsigExperiment isExperimentTest:', isExperimentTest)
  }
  return (
    <>
      <CollectPerformanceMetrics />
      {UUID ? <AuthenticatedApp /> : <UnauthenticatedApp />}
    </>
  )
}
